import React, { useRef, useState, useEffect } from "react";
import { css } from "@emotion/css";
import { Popover } from "react-text-selection-popover";
import { Popover as AntdPopover } from "antd";
import TextareaAutosize from "react-textarea-autosize";
import {
  PlusCircleOutlined,
  SearchOutlined,
  GlobalOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import { CustomMarkdownRenderer } from "../../components/CustomMarkdownRenderer";
import { useIdeasNodeStore } from "../../stores/ideaNodesStore";
import {
  textAreaInnerContainer,
  textAreaInnerContainerOnActionPopover,
  textareaInputBar,
  textareaInputBarOnActionPopover,
  textareaSendButton,
} from "../summary_session_styles";

const SecondPopover = ({
  position,
  selectedText,
  onClose,
}: {
  position: { left: number; top: number };
  selectedText: string;
  onClose: () => void;
}) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const [searchPopoverText, setSearchPopoverText] = useState("");

  const submitChatPaneNewUserUtterance = useIdeasNodeStore(
    (state) => state.submitChatPaneNewUserUtteranceEphemeral
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".second-popover-content")) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [onClose]);

  const handleInteractionStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const popoverStyle = css`
    position: fixed;
    left: ${position.left}px;
    top: ${position.top}px;
    width: 300px;
    background: #272727;
    font-size: 0.7em;
    text-align: center;
    color: white;
    border: 1px solid #0a0a0a;
    border-radius: 7px;
    z-index: 1000;
    // box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    pointer-events: auto;
    -webkit-tap-highlight-color: transparent;
  `;

  const textAreaContainerStyle = css`
    padding: 2px;
  `;

  const textAreaStyle = css`
    width: 90%;
    // min-height: 100px;
    padding: 5px;
    border: 1px solid #ddd;
    border-radius: 3px;
    margin: 2px auto;
    // resize: vertical;
    resize: "none",
    font-size: 14px;
    -webkit-appearance: none;
    &:focus {
      outline: none;
      // border-color: blue;
    }
  `;

  return (
    <div
      className={`${popoverStyle} second-popover-content`}
      onMouseDown={handleInteractionStart}
      onTouchStart={handleInteractionStart}
    >
      <div
        css={[textAreaInnerContainer, textAreaInnerContainerOnActionPopover]}
        // onClick={() => {
        //   // textAreaRef.current?.focus();
        // }}
      >
        <TextareaAutosize
          ref={textAreaRef}
          minRows={1}
          maxRows={5}
          placeholder="Ask..."
          autoFocus={true}
          css={[textareaInputBar, textareaInputBarOnActionPopover]}
          value={searchPopoverText}
          onChange={(e) => setSearchPopoverText(e.target.value)}
          onClick={handleInteractionStart}
          onTouchStart={handleInteractionStart}
          onMouseDown={handleInteractionStart}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              // Add user message to chat pane
              submitChatPaneNewUserUtterance(searchPopoverText, selectedText);

              onClose();
            } else if (e.key === "Escape") {
              onClose();
            }
          }}
        />

        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            submitChatPaneNewUserUtterance(searchPopoverText, selectedText);

            onClose();
          }}
          css={textareaSendButton}
        >
          <ArrowRightOutlined />
        </button>
      </div>

      {/* <div className={textAreaContainerStyle}>
        <TextareaAutosize
          ref={textAreaRef}
          className={textAreaStyle}
          minRows={1}
          maxRows={6}
          value={searchPopoverText}
          onChange={(e) => setSearchPopoverText(e.target.value)}
          onClick={handleInteractionStart}
          onTouchStart={handleInteractionStart}
          onMouseDown={handleInteractionStart}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();

              // Add user message to chat pane
              submitChatPaneNewUserUtterance(searchPopoverText, selectedText);

              onClose();
            } else if (e.key === "Escape") {
              onClose();
            }
          }}
        />
      </div> */}
    </div>
  );
};

export const MarkdownWithHighlightPowers = (props: {
  rawMarkdownContent: string;
}) => {
  const [ref, setRef] = useState<HTMLElement>();
  const [popoverPosition, setPopoverPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const [selectedText, setSelectedText] = useState("");
  const [showSecondPopover, setShowSecondPopover] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const currentTextContent = useRef<string>("");

  const resetPopover = () => {
    setPopoverPosition(null);
    setSelectedText("");
    setShowSecondPopover(false);
    currentTextContent.current = "";
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as HTMLElement;
      if (
        !target.closest(".popover-content") &&
        !target.closest(".second-popover-content")
      ) {
        resetPopover();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  const handleSearchInteraction = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Prevent losing selection
    const selection = window.getSelection();
    const range = selection?.getRangeAt(0);

    // Set states synchronously
    currentTextContent.current = selection?.toString() || "";
    setSelectedText(currentTextContent.current);
    setShowSecondPopover(true);

    // Restore selection if needed
    if (range) {
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  };

  const containerStyle = css`
    position: relative;

    strong {
      background-color: rgb(20 20 20 / 85%);
      color: #1594c0;
      font-weight: 700;
      padding: 2px 5px;
      border-radius: 4px;
      box-decoration-break: clone;
      -webkit-box-decoration-break: clone;
    }

    /* Make the video embeds take up the full width of the container when inside lists */
    ul .ytFrameTopContainer,
    ol .ytFrameTopContainer {
      margin-top: 8px;
      margin-left: -2.1rem;
      width: calc(100% + 2.1rem);
    }

    /* Handle nested lists by adjusting the margin for deeper levels */
    ul ul .ytFrameTopContainer,
    ul ol .ytFrameTopContainer,
    ol ul .ytFrameTopContainer,
    ol ol .ytFrameTopContainer {
      margin-left: -4.2rem;
      width: calc(100% + 4.2rem);
    }

    ul > li {
      margin-bottom: 8px;
    }
  `;

  const popoverDistanceAboveInPx = 45;

  const popoverStyle = (rect: DOMRect | undefined) => css`
    position: fixed;
    left: ${popoverPosition?.left ?? rect?.left}px;
    top: ${popoverPosition?.top ??
    (rect?.top ?? 0) - popoverDistanceAboveInPx}px;
    transform: translateY(0);
    width: 120px;
    background: #272727;
    font-size: 1em;
    text-align: center;
    color: white;
    border: 1px solid #0a0a0a;
    border-radius: 7px;
    z-index: 1000;
    // box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    pointer-events: auto;
    -webkit-tap-highlight-color: transparent;
    touch-action: none;
  `;

  const popoverHeaderStyle = css`
    display: flex;
    align-items: center;
    justify-content: center;
  `;

  const actionIconStyle = css`
    cursor: pointer;
    padding: 8px;
    -webkit-tap-highlight-color: transparent;
    &:hover {
      background: rgba(0, 0, 0, 0.05);
      border-radius: 3px;
      color: #1d72b9;
    }
    &:active {
      background: rgba(0, 0, 0, 0.1);
    }
  `;

  const handleInteractionStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const [
    factCheckFeatureComingSoonPopoverOpen,
    setFactCheckFeatureComingSoonPopoverOpen,
  ] = useState(false);

  const submitChatPaneNewUserUtterance = useIdeasNodeStore(
    (state) => state.submitChatPaneNewUserUtteranceEphemeral
  );

  return (
    <div className={containerStyle} ref={containerRef}>
      <div ref={(el) => el != null && setRef(el)}>
        <CustomMarkdownRenderer rawMarkdownContent={props.rawMarkdownContent} />

        <Popover
          target={ref}
          mount={ref}
          render={({ clientRect, isCollapsed, textContent }) => {
            if (clientRect == null || (isCollapsed && !showSecondPopover))
              return null;

            if (textContent) {
              currentTextContent.current = textContent;
            }

            if (!popoverPosition && clientRect) {
              setPopoverPosition({
                left: clientRect.left,
                top: clientRect.top - popoverDistanceAboveInPx,
              });
            }

            const currentPosition =
              popoverPosition ||
              (clientRect
                ? {
                    left: clientRect.left,
                    top: clientRect.top - popoverDistanceAboveInPx,
                  }
                : null);

            if (!currentPosition) return null;

            const handlePlusCircleInteraction = (
              e: React.MouseEvent | React.TouchEvent
            ) => {
              e.preventDefault();
              e.stopPropagation();

              submitChatPaneNewUserUtterance(
                `Dig deeper into: ${textContent}`,
                textContent
              );

              // Clear the selection to close the popover
              window.getSelection()?.removeAllRanges();
              setRef(null);
            };

            return (
              <>
                <div
                  className={`${popoverStyle(clientRect)} popover-content`}
                  onMouseDown={handleInteractionStart}
                  onTouchStart={handleInteractionStart}
                >
                  <div className={popoverHeaderStyle}>
                    <span
                      className={actionIconStyle}
                      onClick={handlePlusCircleInteraction}
                    >
                      {/* {`${textContent?.length || 0} chars`} */}
                      <PlusCircleOutlined />
                    </span>
                    <span
                      className={actionIconStyle}
                      onMouseDown={handleSearchInteraction}
                      onTouchStart={handleSearchInteraction}
                    >
                      <SearchOutlined />
                    </span>
                    <span className={actionIconStyle}>
                      <AntdPopover
                        content={
                          <div
                            className={css`
                              color: white;
                              padding: 0 8px;
                            `}
                          >
                            Fact checking coming soon...
                          </div>
                        }
                        open={factCheckFeatureComingSoonPopoverOpen}
                        onOpenChange={(newOpen) =>
                          setFactCheckFeatureComingSoonPopoverOpen(newOpen)
                        }
                      >
                        <GlobalOutlined />
                      </AntdPopover>
                    </span>
                  </div>
                </div>

                {showSecondPopover && currentPosition && (
                  <SecondPopover
                    position={currentPosition}
                    selectedText={currentTextContent.current}
                    onClose={() => setShowSecondPopover(false)}
                  />
                )}
              </>
            );
          }}
        />
      </div>
    </div>
  );
};
